import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Form, Formik, FormikHelpers } from 'formik'
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Asset, AssetInput, Lift } from '../types'
import { updateAsset } from '../services/assetService'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { getLiftsByProjectId } from '../services/liftService'

export interface AssetLiftFormProps {
  asset: Asset
  beforeSubmit?: (values: AssetInput) => void
  afterSubmit?: (asset: Asset) => void
  onCancel?: () => void
}

export default function AssetLiftForm({
  asset,
  beforeSubmit,
  afterSubmit,
  onCancel
}: AssetLiftFormProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('asset')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The lifts state.
   */
  const [lifts, setLifts] = useState<Lift[]>([])

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * The initial form values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId
  }

  /**
   * Load the lifts.
   */
  async function loadLifts(): Promise<void> {
    try {
      setLoading(true)

      if (asset.projectId !== null) {
        setLifts(
          (await getLiftsByProjectId(asset.projectId)).sort(
            (a, b) => (a.number ?? 0) - (b.number ?? 0)
          )
        )
      } else {
        setLifts([])
      }
    } catch (_) {
      // Do nothing.
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle the "submit" event.
   */
  async function handleSubmit(values: AssetInput, { setSubmitting }: FormikHelpers<AssetInput>) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values)

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('updateAssetSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset)
    } catch (_) {
      dispatch(createErrorNotification(t('updateAssetError')))
      setSubmitting(false)
    }
  }

  /**
   * Load the lifts when the component mounts.
   */
  useEffect(() => {
    loadLifts()
  }, [asset?.projectId])

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue, setFieldTouched, isSubmitting, handleChange, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="lift-select-label">{t('lift')}</InputLabel>
                <Select
                  id="lift-select"
                  labelId="lift-select-label"
                  name="lift"
                  label={t('lift')}
                  value={values.lift}
                  disabled={loading || isSubmitting}
                  autoFocus
                  onChange={handleChange}
                >
                  {lifts.map((lift) => (
                    <MenuItem key={lift.id} value={lift.id}>
                      {lift.name}
                    </MenuItem>
                  ))}
                </Select>
                {values.lift !== null && (
                  <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      disabled={isSubmitting}
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setFieldValue('lift', null)
                        setFieldTouched('lift', true)
                      }}
                    >
                      {t('clearSelection')}
                    </Button>
                  </Stack>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onCancel}>
                  {t('cancel')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                >
                  {t('save')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
